/**
 * Les huit niveaux d'adversaire virtuel — portage de `sim/niveaux.py`.
 *
 * Le simulateur Python est la source de vérité des profils : c'est lui qui les
 * mesure (`sim/recalibrer.py`). Ce module doit rendre **exactement** les mêmes
 * décisions, ce que `tests/niveaux.test.ts` vérifie sur 5 376 décisions exportées.
 *
 * ## Les trois couches, et leur ordre
 *
 * Une échelle de difficulté faite de stratégies nues donne des adversaires faibles,
 * pas des adversaires humains : ils jouent mal, mais toujours pareil, et
 * instantanément. D'où trois couches (plan § 4.2), appliquées dans cet ordre — et
 * l'ordre compte, le changer changerait le jeu :
 *
 *   1. le **tempérament** peut décider seul (s'arrêter, sécher, chasser le 421) ;
 *   2. sinon la **stratégie** du niveau décide ;
 *   3. l'**erreur** ε peut remplacer le résultat par la deuxième meilleure action.
 *
 * Le **tempo** (temps de réflexion) est la quatrième couche, mais elle ne change
 * aucune décision : elle vit ici seulement, et n'existe pas côté Python où la
 * simuler serait du calcul dépensé pour rien.
 *
 * ## Ce qui vient des tables
 *
 * On ne recalcule aucune programmation dynamique en TypeScript. Les niveaux 6 et 8
 * lisent les tables exportées (`equilibre`, `seuil`, `glouton`), et l'erreur ε lit
 * `secondes`. Les niveaux 1 à 5 et 7 sont des heuristiques, portées ligne à ligne.
 */
import { evaluerMain } from "./combinaisons.js";
import { objectifDe, TablesIncompatibles, valeursVersIndices, } from "./ia.js";
/** Score de la main 665 : le seuil de sec le plus rentable mesuré (plan § 3.4). */
export const SEUIL_SEC_665 = 665;
/** Repères de score utilisés par les heuristiques (mêmes valeurs qu'en Python). */
const S_421 = 1000;
const S_BRELAN_MIN = 820;
const S_SUITE_MIN = 703;
const NEUTRE = {
    nom: "neutre", chasse421: false, arreteSiDevant: false, secheA: null, bavard: false,
};
const FLAMBEUR = {
    nom: "flambeur", chasse421: true, arreteSiDevant: false, secheA: null, bavard: true,
};
const PRUDENT = {
    nom: "prudent", chasse421: false, arreteSiDevant: true, secheA: null, bavard: false,
};
const TATILLON = {
    nom: "tatillon", chasse421: false, arreteSiDevant: false, secheA: SEUIL_SEC_665,
    bavard: false,
};
const TATILLON_BAVARD = { ...TATILLON, bavard: true };
/**
 * Les huit niveaux, du plus faible au plus fort — **ordonnés par taux de défaite
 * mesuré** (`sim/CALIBRATION.md`), pas par intuition sur les stratégies.
 *
 * ⚠️ L'ordre des niveaux 2 et 3 est inversé par rapport au § 4.1 du plan, et ce n'est
 * pas un détail. Mesuré : sans doublement du sec, « garde les as sans jamais
 * s'arrêter » devance « jamais les as » de 7 points. **Avec le doublement, les deux
 * sont à égalité stricte** (51,96 % contre 51,91 %) : le doublement raccourcit les
 * parties et profite surtout à qui s'arrête parfois, donc au distrait, qui gagne
 * 7,7 points quand l'acharné n'en gagne que 0,6. C'est désormais le taux d'erreur de
 * L'acharné qui les sépare.
 *
 * ε n'est donc pas monotone sur les trois premiers niveaux : ce qui doit décroître,
 * c'est le taux de défaite. ε est un levier, pas l'objectif. À partir du niveau 4 il
 * décroît régulièrement, et c'est là qu'il affine l'échelle.
 */
export const PROFILS = [
    { niveau: 1, cle: "presse", strategie: "sec", epsilon: 0, temperament: NEUTRE },
    { niveau: 2, cle: "acharne", strategie: "asSansArret", epsilon: 0.12, temperament: NEUTRE },
    { niveau: 3, cle: "distrait", strategie: "jamaisLesAs", epsilon: 0, temperament: NEUTRE },
    { niveau: 4, cle: "habitue", strategie: "gardeLesAs", epsilon: 0.09, temperament: NEUTRE },
    { niveau: 5, cle: "flambeur", strategie: "asEtChasse421", epsilon: 0.07, temperament: FLAMBEUR },
    { niveau: 6, cle: "calculateur", strategie: "glouton", epsilon: 0.05, temperament: PRUDENT },
    { niveau: 7, cle: "patron", strategie: "memo", epsilon: 0.02, temperament: TATILLON_BAVARD },
    { niveau: 8, cle: "taulier", strategie: "optimalePhase", epsilon: 0, temperament: TATILLON },
];
export function profilDe(niveau) {
    const profil = PROFILS.find((p) => p.niveau === niveau);
    if (!profil)
        throw new Error(`niveau d'IA inconnu : ${String(niveau)}`);
    return profil;
}
// ------------------------------------------------------------------ utilitaires
/** Valeurs de la main triées en décroissant, comme `rules.HANDS`. */
function decroissant(main) {
    return [...main].sort((a, b) => b - a);
}
function compte(des, valeur) {
    return des.filter((d) => d === valeur).length;
}
/**
 * Groupes de la main, du plus gros effectif à la plus haute valeur — équivalent de
 * `strategies._groups`. À effectif égal, la valeur la plus haute passe devant.
 */
function groupes(des) {
    const valeurs = [...new Set(des)].sort((a, b) => b - a);
    return valeurs
        .map((valeur) => ({ n: compte(des, valeur), valeur }))
        .sort((a, b) => b.n - a.n || b.valeur - a.valeur);
}
const HEURISTIQUES = {
    jamaisLesAs: { gardeLesAs: false, chasse421: false, gardeLesPaires: true, sArreteA: S_BRELAN_MIN },
    asSansArret: { gardeLesAs: true, chasse421: false, gardeLesPaires: true, sArreteA: S_421 + 1 },
    gardeLesAs: { gardeLesAs: true, chasse421: false, gardeLesPaires: true, sArreteA: S_BRELAN_MIN },
    asEtChasse421: { gardeLesAs: true, chasse421: true, gardeLesPaires: true, sArreteA: S_BRELAN_MIN },
};
/** Famille de règles « de bistrot » — portage de `strategies.Heuristique.keep`. */
function heuristique(reglages, des, score) {
    if (score >= reglages.sArreteA)
        return des;
    let gardes = [];
    if (reglages.gardeLesAs && des.includes(1)) {
        gardes = Array(compte(des, 1)).fill(1);
        if (reglages.chasse421 && gardes.length === 1) {
            for (const valeur of [4, 2]) {
                if (des.includes(valeur) && gardes.length < 3)
                    gardes.push(valeur);
            }
        }
    }
    else if (reglages.gardeLesPaires) {
        const plusGros = groupes(des)[0];
        if (plusGros.n >= 2)
            gardes = Array(plusGros.n).fill(plusGros.valeur);
    }
    if (gardes.length === 3)
        return des;
    return gardes.sort((a, b) => b - a);
}
/**
 * Les cinq règles du niveau 7 — portage de `strategies.Memo.keep`.
 *
 * L'intérêt n'est pas d'égaler la DP mais d'appliquer une politique qu'un humain
 * peut tenir de tête. La règle la plus contre-intuitive, et la plus rentable : une
 * paire de 5 ou moins ne vaut pas d'être gardée.
 */
function memo(des, score) {
    const as = compte(des, 1);
    // Combinaison faite : suite, brelan, 611 et au-dessus. Les paires d'as basses
    // (211 à 511) ne comptent pas : la DP les relance encore.
    if (score >= S_SUITE_MIN && !(as === 2 && score < 865))
        return des;
    if (as >= 2)
        return [1, 1];
    if (compte(des, 6) >= 2)
        return [6, 6];
    if (as === 1)
        return des.includes(2) ? [2, 1] : [1];
    if (des.includes(6))
        return des.includes(5) ? [6, 5] : [6];
    return [];
}
/** Nombre d'adversaires, borné aux tables disponibles (1 à 5). */
function adversaires(ctx) {
    return Math.max(1, Math.min(5, ctx.joueursActifs - 1));
}
function lireTable(table, cle, relances, quoi) {
    if (!table)
        throw new TablesIncompatibles(`table absente : ${quoi}`);
    const parRelance = table[cle];
    if (!parRelance)
        throw new TablesIncompatibles(`main ${cle} absente de ${quoi}`);
    const decision = parRelance[relances - 1];
    if (!decision)
        throw new TablesIncompatibles(`relance ${relances} absente de ${quoi}`);
    return decision;
}
/**
 * Positions canoniques -> valeurs. Les tables donnent des positions dans la main
 * triée en décroissant ; on les retraduit en valeurs pour rester comparable au
 * Python, qui raisonne en valeurs.
 */
function positionsEnValeurs(des, positions) {
    return positions.map((p) => des[p]).sort((a, b) => b - a);
}
/** Niveau 8 — portage de `strategies.Optimale` en mode « phase ». */
function optimalePhase(tables, des, relances, ctx) {
    const cleMain = evaluerMain(des).cle;
    const objectif = objectifDe(ctx.phase);
    if (ctx.scoresPoses.length > 0 && ctx.joueursApres === 0) {
        // On parle en dernier : l'information est complète, l'objectif devient un simple
        // seuil à dépasser et la DP est alors exactement optimale.
        const seuil = objectif === "charge"
            ? Math.min(...ctx.scoresPoses)
            : Math.max(...ctx.scoresPoses);
        return positionsEnValeurs(des, lireTable(tables.seuil[String(seuil)], cleMain, relances, `seuil ${seuil}`));
    }
    const k = adversaires(ctx);
    return positionsEnValeurs(des, lireTable(tables.equilibre[objectif]?.[String(k)], cleMain, relances, `equilibre ${objectif} k=${k}`));
}
/** Niveau 6 — politique myope, indépendante des relances restantes. */
function glouton(tables, des) {
    const cleMain = evaluerMain(des).cle;
    const positions = tables.glouton?.[cleMain];
    if (!positions) {
        throw new TablesIncompatibles(`politique gloutonne absente pour ${cleMain} — tables trop anciennes ?`);
    }
    return positionsEnValeurs(des, positions);
}
// --------------------------------------------------------------- les couches
/** Décision imposée par le tempérament, ou `null` s'il laisse jouer la stratégie. */
function temperamentDecide(t, des, score, ctx) {
    if (t.secheA !== null && ctx.premier && score >= t.secheA) {
        // Sécher en ouvrant : tout le monde n'aura qu'un lancer.
        return des;
    }
    if (t.arreteSiDevant && ctx.scoresPoses.length > 0 && ctx.phase === "CHARGE") {
        // En charge, le danger est d'être le plus faible : dès qu'on ne l'est plus,
        // relancer ne fait que risquer de le redevenir. La règle ne vaut QUE en charge :
        // en décharge il faut être le plus fort, et « être devant le plus faible » ne
        // protège de rien — c'est l'erreur la plus chère du 421.
        if (score > Math.min(...ctx.scoresPoses))
            return des;
    }
    if (t.chasse421 && compte(des, 1) === 1 && score < 700) {
        // Un as seul, rien de fait : on garde l'as et ce qui va avec vers le 421.
        const gardes = [1];
        for (const valeur of [4, 2]) {
            if (des.includes(valeur) && gardes.length < 3)
                gardes.push(valeur);
        }
        if (gardes.length >= 2)
            return gardes.sort((a, b) => b - a);
    }
    return null;
}
/** Deuxième meilleure action de la politique d'équilibre du contexte. */
function seconde(tables, des, relances, ctx) {
    const cleMain = evaluerMain(des).cle;
    const objectif = objectifDe(ctx.phase);
    const k = adversaires(ctx);
    const table = tables.secondes?.[objectif]?.[String(k)];
    if (!table) {
        throw new TablesIncompatibles(`deuxièmes meilleures actions absentes (${objectif}, k=${k}) — tables trop anciennes ?`);
    }
    return positionsEnValeurs(des, lireTable(table, cleMain, relances, `secondes ${objectif} k=${k}`));
}
function strategieDecide(strategie, tables, des, score, relances, ctx) {
    switch (strategie) {
        case "sec":
            return des;
        case "glouton":
            return glouton(tables, des);
        case "memo":
            return memo(des, score);
        case "optimalePhase":
            return optimalePhase(tables, des, relances, ctx);
        default:
            return heuristique(HEURISTIQUES[strategie], des, score);
    }
}
/**
 * Valeurs des dés à garder, dans l'ordre décroissant.
 *
 * `alea` est le tirage uniforme [0, 1[ qui décide de l'erreur. Il est **injecté** et
 * non tiré ici : c'est ce qui rend la décision reproductible et permet de la
 * comparer au Python sur des séquences fournies (plan § 8, pas de PRNG partagé).
 * Passer 1 (ou toute valeur ≥ ε) donne le jeu sans erreur.
 */
export function valeursAGarder(profil, tables, main, relances, ctx, alea = 1) {
    const des = decroissant(main);
    const score = evaluerMain(main).score;
    let decision = temperamentDecide(profil.temperament, des, score, ctx);
    if (decision === null) {
        decision = strategieDecide(profil.strategie, tables, des, score, relances, ctx);
    }
    if (profil.epsilon > 0 && alea < profil.epsilon) {
        const autre = seconde(tables, des, relances, ctx);
        // Si la « deuxième » est celle qu'on jouait déjà, l'erreur n'a pas d'effet : on
        // ne cherche pas à forcer une différence.
        decision = autre;
    }
    return decision;
}
/**
 * Dés à garder, **en index du tapis** — ce que le moteur consomme.
 *
 * La traduction valeurs -> index est le seul endroit où un bug resterait muet : la
 * décision serait juste et le dé gardé faux. C'est pourquoi elle est isolée ici et
 * testée séparément, y compris sur des mains à doublons.
 */
export function gardeDuNiveau(profil, tables, main, relances, ctx, alea = 1) {
    return valeursVersIndices(main, valeursAGarder(profil, tables, main, relances, ctx, alea));
}
// ------------------------------------------------------------------- le tempo
/**
 * Temps de réflexion, en secondes.
 *
 * Purement cosmétique — aucune décision n'en dépend, et le simulateur l'ignore. Mais
 * c'est ce qui fait la différence entre un adversaire et un automate : une IA qui
 * répond en 30 ms ne joue pas, elle calcule.
 *
 * Loi log-normale, plus lente quand la décision est serrée. `serrement` va de 0 (un
 * choix évident) à 1 (deux actions de valeur voisine) : l'hésitation devient alors
 * crédible sans rien coûter, puisque la table donne déjà les deux meilleures.
 */
export function tempoDeReflexion(profil, serrement, alea) {
    const base = 0.45 + 0.11 * (8 - profil.niveau);
    const serre = Math.min(1, Math.max(0, serrement));
    // Médiane allongée jusqu'à +90 % quand c'est serré.
    const mediane = base * (1 + 0.9 * serre);
    // Log-normale obtenue par une normale approchée : une somme d'uniformes suffit
    // largement pour un tempo, et reste déterministe à partir d'`alea`.
    const u = Math.min(0.9999, Math.max(0.0001, alea));
    const normale = Math.log(u / (1 - u)) * 0.32;
    const duree = mediane * Math.exp(normale);
    // Bornes : en dessous ça paraît automatique, au-dessus le joueur croit à un bug.
    return Math.min(4.5, Math.max(0.3, duree));
}
//# sourceMappingURL=niveaux.js.map